import React, { useState } from "react"
import { ChevronDown, ChevronRight, Info } from "lucide-react"

import { Badge } from "@/components/ui/badge"

// Legend for the quarterly performance metrics, shown on MyProviderData's Quarterly Performance
// section and on the EPR & Metrics admin page.
//
// Everything here comes from dbo.EPR_MetricDefinition (name, target, Explanation), so when a
// metric's rules change, the text is updated in Sql/Metrics/02_Seed_MetricDefinition.sql - this
// component never needs to change for it. Explanation holds one point per line.

export interface MetricLegendItem {
  key: string
  name: string
  targetLabel: string
  isComingSoon: boolean
  explanation?: string | null
}

interface Props {
  metrics: MetricLegendItem[]
  defaultOpen?: boolean
}

const MetricLegend: React.FC<Props> = ({ metrics, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen)

  if (metrics.length === 0) return null

  return (
    <div className="rounded-md border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left font-semibold"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Info className="h-4 w-4 text-muted-foreground" />
        How each metric is measured
      </button>

      {open && (
        <div className="grid gap-4 border-t p-4 md:grid-cols-2">
          {metrics.map((m) => {
            const lines = (m.explanation ?? "")
              .split(/\r?\n/)
              .map((l) => l.trim())
              .filter(Boolean)

            return (
              <div key={m.key}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{m.name}</span>
                  <Badge variant="outline">Target {m.targetLabel}</Badge>
                  {m.isComingSoon && <Badge variant="secondary">Coming Soon</Badge>}
                </div>
                {lines.length > 0 ? (
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
                    {lines.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-1 text-sm text-muted-foreground">No description yet.</div>
                )}
              </div>
            )
          })}

          <div className="text-sm text-muted-foreground md:col-span-2">
            N/A means there was nothing to measure for that period. N/A metrics are left out of
            the quarterly performance score.
          </div>
        </div>
      )}
    </div>
  )
}

export default MetricLegend
